<template>
    <div>
        <!-- Creator Profile Modal -->
        <Dialog :open="route.query.modal === 'creator_affinity_breakdown'" @update:open="onCreatorProfileModalClose">
            <DialogScrollContent :class="'max-w-5xl border-none p-0'">
                <CreatorProfile
                    v-if="route.query.platform_id && route.query.platform_user_id"
                    :platform-id="route.query.platform_id"
                    :platform-user-id="route.query.platform_user_id"
                    :targeting-groups="[]"
                />
            </DialogScrollContent>
        </Dialog>

        <div class="mx-auto w-full max-w-9xl p-3 md:p-4 lg:p-8 h-full">
        <!-- Header Section with improved styling -->
        <div class="mb-6">
            <!-- Breadcrumb navigation with better design -->
            <nav class="flex mb-4" aria-label="Breadcrumb">
                <ol class="inline-flex items-center space-x-1 md:space-x-2">
                    <li class="inline-flex items-center">
                        <router-link
                            to="/industries"
                            class="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-indigo-600 transition-colors"
                        >
                            <AppIcon name="chart-line" class="h-3.5 w-3.5" />
                            <span>Industries</span>
                        </router-link>
                    </li>
                    <li>
                        <div class="flex items-center">
                            <AppIcon name="chevron-right" class="w-3 h-3 text-slate-400 mx-1" />
                            <span class="text-sm font-medium text-slate-500">{{ industryName }}</span>
                        </div>
                    </li>
                    <li v-if="categoryName">
                        <div class="flex items-center">
                            <AppIcon name="chevron-right" class="w-3 h-3 text-slate-400 mx-1" />
                            <span class="text-sm font-medium text-slate-500">{{ categoryName }}</span>
                        </div>
                    </li>
                </ol>
            </nav>

            <!-- Title Section with icon and gradient -->
            <div class="flex items-start justify-between">
                <div class="flex items-center gap-3">
                    <div class="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
                        <span class="text-2xl">{{ getIndustryIcon(industryName) }}</span>
                    </div>
                    <div>
                        <h1 class="text-3xl font-bold text-slate-900 mb-1">
                            {{ categoryName ? `${industryName} - ${categoryName}` : industryName }}
                        </h1>
                        <div class="flex items-center gap-2 text-sm text-slate-600">
                            <span>Top {{ creators.length }} creators</span>
                            <span v-if="totalCount > creators.length" class="text-slate-400">• {{ totalCount.toLocaleString() }} total available</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Category Navigation with Platform Selection -->
        <div class="mb-6 bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div class="bg-slate-50 px-5 py-3 border-b border-slate-200">
                <div class="flex items-center justify-between">
                    <h3 v-if="availableCategories.length > 0" class="text-sm font-semibold text-slate-900">Categories in {{ industryName }}</h3>
                    <h3 v-else class="text-sm font-semibold text-slate-900">{{ industryName }}</h3>

                    <!-- Platform Selection with improved styling -->
                    <div class="flex items-center gap-2">
                        <div class="inline-flex rounded-lg bg-slate-100 p-1" role="group">
                            <button
                                class="flex rounded-md px-3 py-2 text-sm font-bold leading-tight transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                :class="platform === 'youtube'
                                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md'
                                    : 'text-slate-600 hover:bg-slate-50'"
                                @click.prevent="onPlatformClick('youtube')"
                            >
                                <AppIcon class="h-4 w-4" name="youtube" />
                            </button>

                            <button
                                class="flex rounded-md px-3 py-2 text-sm font-bold leading-tight transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                :class="platform === 'twitch'
                                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md'
                                    : 'text-slate-600 hover:bg-slate-50'"
                                @click.prevent="onPlatformClick('twitch')"
                            >
                                <AppIcon class="h-4 w-4" name="twitch" />
                            </button>

                            <button
                                class="flex rounded-md px-3 py-2 text-sm font-bold leading-tight transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                :class="platform === 'tiktok'
                                    ? 'bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-md'
                                    : 'text-slate-600 hover:bg-slate-50'"
                                @click.prevent="onPlatformClick('tiktok')"
                            >
                                <AppIcon class="h-4 w-4" name="tiktok" />
                            </button>

                            <button
                                class="flex rounded-md px-3 py-2 text-sm font-bold leading-tight transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                :class="platform === 'twitter'
                                    ? 'bg-x text-white shadow-md'
                                    : 'text-slate-600 hover:bg-slate-50'"
                                @click.prevent="onPlatformClick('twitter')"
                            >
                                <AppIcon class="h-4 w-4" name="x-twitter" />
                            </button>

                            <button
                                class="flex rounded-md px-3 py-2 text-sm font-bold leading-tight transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                :class="platform === 'instagram'
                                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md'
                                    : 'text-slate-600 hover:bg-slate-50'"
                                @click.prevent="onPlatformClick('instagram')"
                            >
                                <AppIcon class="h-4 w-4" name="instagram" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Category Pills -->
            <div v-if="availableCategories.length > 0" class="p-4">
                <div class="flex flex-wrap gap-2">
                    <!-- All Categories option -->
                    <router-link
                        :to="{ name: 'industry-detail', params: { industry_name: industryName } }"
                        class="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-[background-color,border-color] border"
                        :class="!categoryName
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-transparent'
                            : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'"
                    >
                        All Categories
                    </router-link>

                    <!-- Individual category options -->
                    <router-link
                        v-for="category in availableCategories"
                        :key="category.code"
                        :to="{ name: 'industry-category-detail', params: { industry_name: industryName, category_name: category.code } }"
                        class="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-[background-color,border-color] border"
                        :class="categoryName === category.code
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-transparent'
                            : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'"
                    >
                        <span v-if="category.emoji || category.icon" class="mr-1.5">{{ category.emoji || category.icon }}</span>
                        {{ category.label }}
                    </router-link>
                </div>
            </div>
        </div>

        <!-- Creators table with improved styling -->
        <div v-if="creators.length > 0 || isLoading" class="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">

            <IndustryTable
                :creators="creators"
                :is-loading="isLoading"
                :is-busy="false"
                :columns="tableColumns"
                :enabled-columns="enabledColumns"
                :paging="pagination"
                :is-selectable="false"
                id-prop="_id"
                superview-path="superview"
                :platform-id="platform"
                :row-background-class="getRowBackgroundClass"
                @filter-change="onFilterChange"
                @paging-change="onPagingChange"
            >
                <template #header-toolbar-right>
                    <div class="flex items-center gap-2">
                        <!-- Content Type Selector -->
                        <div class="flex flex-col">
                            <label for="content-type-select" class="text-xs font-semibold text-slate-600 mb-1">Content Type</label>
                            <Select v-model="contentType">
                                <SelectTrigger id="content-type-select" class="w-[180px]">
                                    <SelectValue placeholder="Select Content Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem v-for="option in contentTypeOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <!-- Day Range Selector -->
                        <div class="flex flex-col">
                            <label for="day-range-select" class="text-xs font-semibold text-slate-600 mb-1">Day Range</label>
                            <Select v-model.number="dayRange">
                                <SelectTrigger id="day-range-select" class="w-[180px]">
                                    <SelectValue placeholder="Select Day Range" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem v-for="option in dayRangeOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </template>

                <template #ranking="{ index }">
                    <div class="flex items-center justify-center">
                        <!-- Top 3 with special badges -->
                        <div v-if="getRanking(index) === 1" class="flex items-center justify-center gap-2">
                            <div class="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full shadow-lg">
                                <AppIcon name="crown" class="h-4 w-4 text-white" />
                            </div>
                            <span class="text-xl font-bold text-amber-600">1</span>
                        </div>
                        <div v-else-if="getRanking(index) === 2" class="flex items-center justify-center gap-2">
                            <div class="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full shadow-lg">
                                <AppIcon name="medal" class="h-4 w-4 text-white" />
                            </div>
                            <span class="text-xl font-bold text-gray-600">2</span>
                        </div>
                        <div v-else-if="getRanking(index) === 3" class="flex items-center justify-center gap-2">
                            <div class="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full shadow-lg">
                                <AppIcon name="award" class="h-4 w-4 text-white" />
                            </div>
                            <span class="text-xl font-bold text-orange-600">3</span>
                        </div>
                        <!-- Rest with regular numbers -->
                        <div v-else class="text-center font-semibold text-slate-700">
                            <span class="text-sm">#{{ getRanking(index) }}</span>
                        </div>
                    </div>
                </template>

                <template #country="{ item }">
                    <div class="text-center">
                        <span class="text-lg text-slate-700">
                            {{ getCountryCode(item) }}
                        </span>
                    </div>
                </template>

                <template #followers="{ item }">
                    <!-- Show skeleton while loading cross-platform data -->
                    <div v-if="isLoadingCrossPlatform" class="flex flex-col space-y-2">
                        <div v-for="n in 4" :key="n" class="flex items-center space-x-2">
                            <div class="w-5 h-5 rounded-full bg-slate-200 animate-pulse"></div>
                            <div class="h-4 bg-slate-200 rounded animate-pulse" :style="`width: ${Math.random() * 30 + 40}px`"></div>
                        </div>
                    </div>

                    <!-- Show actual data when loaded -->
                    <div v-else class="flex flex-col space-y-2">
                        <div v-for="(platformData, platformId) in getFollowersData(item)" :key="platformId" class="flex items-center space-x-2">
                            <div class="flex items-center justify-center w-5 h-5 rounded-full" :class="getPlatformBgClass(platformId)">
                                <AppIcon
                                    :name="getPlatformIcon(platformId)"
                                    class="h-3 w-3 text-white"
                                />
                            </div>
                            <span class="text-sm font-medium text-slate-700">
                                {{ formatFollowerCount(platformData.followers) }}
                            </span>
                        </div>
                        <div v-if="getHiddenPlatformsCount(item) > 0" class="text-xs text-slate-500">
                            +{{ getHiddenPlatformsCount(item) }} more
                        </div>
                    </div>
                </template>

                <template #avg_views="{ item }">
                    <div class="text-center">
                        <span class="text-sm font-medium text-slate-700">
                            {{ formatNumber(item.discovery.avg_views) }}
                        </span>
                    </div>
                </template>

                <template #avg_viewers="{ item }">
                    <div class="text-center">
                        <span class="text-sm font-medium text-slate-700">
                            {{ formatNumber(item.discovery.avg_viewers) }}
                        </span>
                    </div>
                </template>

                <template #creator="{ field, item }">
                    <div class="flex w-full items-center justify-between">
                        <div class="flex flex-col w-full py-4">
                            <router-link
                                class="flex"
                                :to="{
                                    path: $route.path,
                                    query: {
                                        ...$route.query,
                                        modal: 'creator_affinity_breakdown',
                                        platform_id: platform,
                                        platform_user_id: field.id,
                                    },
                                }"
                            >
                                <NamePlate
                                    class="my-auto"
                                    :logo="field.logo"
                                    :name="field.name"
                                    :display-name="field.display_name"
                                    :platform-id="platform"
                                    :platform-user-id="field.id"
                                    :is-drawer-enabled="false"
                                    :is-interactive="true"
                                />
                            </router-link>

                            <!-- Biography snippet -->
                            <div class="mt-2 ml-8 w-11/12">
                                <!-- Show skeleton while loading cross-platform data -->
                                <div v-if="isLoadingCrossPlatform" class="space-y-1">
                                    <div class="h-3 bg-slate-200 rounded animate-pulse"></div>
                                    <div class="h-3 bg-slate-200 rounded animate-pulse w-3/4"></div>
                                </div>

                                <!-- Show actual biography when loaded -->
                                <p v-else-if="item.biography" class="text-xs text-slate-500 leading-relaxed whitespace-normal">
                                    {{ getBiographySnippet(item.biography) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </template>

                <template #industries="{ item }">
                    <div class="industries-dropdown-container flex flex-col space-y-2 relative">
                        <!-- Show skeleton while loading cross-platform data -->
                        <div v-if="isLoadingCrossPlatform" class="space-y-2">
                            <div class="h-6 bg-slate-200 rounded animate-pulse w-20"></div>
                        </div>

                        <!-- Show classifications when loaded -->
                        <div v-else-if="item.classifications && Object.keys(item.classifications).length > 0">
                            <div class="flex items-center gap-1.5 flex-wrap">
                                <!-- Show only category pills -->
                                <span
                                    v-for="category in getMainCategories(item)"
                                    :key="category"
                                    class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-100"
                                >
                                    {{ category }}
                                </span>

                                <!-- +X more button -->
                                <button
                                    v-if="hasMoreClassifications(item)"
                                    class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium text-indigo-600 hover:bg-indigo-50 cursor-pointer transition-colors"
                                    @click="toggleIndustriesDropdown(item)"
                                >
                                    {{ getMoreClassificationsText(item) }}
                                </button>
                            </div>

                            <!-- Expandable dropdown for additional classifications -->
                            <div
                                v-if="expandedCreatorId === getCreatorId(item)"
                                class="industries-dropdown absolute z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-64 max-w-80"
                                style="top: 100%; left: 0;"
                            >
                                <div class="space-y-2">
                                    <div
                                        v-for="(categories, industry) in item.classifications"
                                        :key="industry"
                                        class="border-l-3 border-indigo-200 pl-2"
                                    >
                                        <!-- Industry header with icon -->
                                        <div class="flex items-center mb-1">
                                            <span class="text-base mr-2">{{ getIndustryIcon(industry) }}</span>
                                            <h4 class="text-xs font-medium text-slate-800">{{ industry }}</h4>
                                        </div>

                                        <!-- Categories for this industry -->
                                        <div class="flex flex-wrap gap-1 ml-5">
                                            <span
                                                v-for="category in categories"
                                                :key="category"
                                                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700"
                                            >
                                                {{ category }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Fallback if no classifications -->
                        <div v-else class="text-xs text-slate-400">
                            No classifications
                        </div>
                    </div>
                </template>
            </IndustryTable>

            <!-- See all creators link with better styling -->
            <div v-if="totalCount > creators.length" class="bg-slate-50 border-t border-slate-200 p-5 text-center">
                <p class="text-sm text-slate-600 mb-3">
                    Showing top {{ creators.length }} of {{ totalCount.toLocaleString() }} creators in this {{ categoryName ? 'category' : 'industry' }}.
                </p>
                <p class="text-sm text-slate-600 mb-4">
                    Want to see all {{ totalCount.toLocaleString() }} creators?
                </p>
                <button
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-colors"
                    @click="goToDiscoveryPage"
                >
                    <span>View All in Discovery</span>
                    <AppIcon name="arrow-right" class="h-3.5 w-3.5" />
                </button>
            </div>
        </div>

        <!-- Empty state with improved design -->
        <div v-else class="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <div class="p-5 bg-slate-100 rounded-lg inline-block mb-4">
                <AppIcon name="users" class="h-12 w-12 text-slate-400" />
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">No creators found</h3>
            <p class="text-slate-500 mb-4">
                No creators found for this {{ categoryName ? 'category' : 'industry' }}.
            </p>
            <router-link
                to="/industries"
                class="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-colors"
            >
                <AppIcon name="arrow-left" class="h-3.5 w-3.5" />
                <span>Back to Industries</span>
            </router-link>
        </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { axios } from '@/utils/axios'
import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import NamePlate from '@/components/plates/NamePlate.vue'
import { Dialog, DialogScrollContent } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import CreatorProfile from '@/partials/profiles/CreatorProfile/CreatorProfile.vue'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import IndustryTable from '@/partials/tables/IndustryTable.vue'
import _ from 'lodash'
import { useStore } from 'vuex'

const route = useRoute()
const router = useRouter()
const store = useStore()
const { proxy } = getCurrentInstance()

const isLoadingCreators = ref(false)
const isLoadingCrossPlatform = ref(false)
const creators = ref([])
const totalCount = ref(0)
const availableCategories = ref([])

// Industries dropdown state
const expandedCreatorId = ref(null)

// Computed property for overall loading state (for backward compatibility)
const isLoading = computed(() => isLoadingCreators.value)

// Default platform - using YouTube to match Industries page
const defaultPlatformId = computed(() => store.state.self.defaultPlatformId)
const platform = ref(defaultPlatformId.value)
const platformContentTypeMap = {
    'youtube': 'video',
    'twitch' : 'stream',
    'instagram' : 'post',
    'tiktok' : 'video',
    'twitter' : 'post',
}

// Reactive values for content type and aggregation window
const contentType = ref(null) // Will be initialized based on platform
const dayRange = ref(365) // Default to 365 days (1 year)

// Content type options based on platform
const contentTypeOptions = computed(() => {
    const platformType = platform.value
    if (platformType === 'youtube') {
        return [
            { label: 'Videos', value: 'video' },
            { label: 'Shorts', value: 'short' },
            { label: 'Streams', value: 'stream' }
        ]
    } else if (platformType === 'twitch') {
        return [
            { label: 'Streams', value: 'stream' }
        ]
    } else if (platformType === 'instagram') {
        return [
            { label: 'Posts', value: 'post' },
            { label: 'Reels', value: 'reel' }
        ]
    } else if (platformType === 'tiktok') {
        return [{ label: 'Videos', value: 'video' }]
    } else if (platformType === 'twitter') {
        return [{ label: 'Posts', value: 'post' }]
    }
    return [{ label: 'Default', value: platformContentTypeMap[platformType] }]
})

// Aggregation window options
const dayRangeOptions = [
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 },
    { label: '180 Days', value: 180 },
    { label: '1 Year', value: 365 },
]

const industryName = computed(() => route.params.industry_name)
const categoryName = computed(() => route.params.category_name)

// Table configuration to match DiscoverPage
const tableColumns = computed(() => [
    {
        id: 'ranking',
        key: 'ranking',
        header: {
            label: '#',
            is_sortable: false,
            is_hideable: false,
            class: 'w-16',
        },
        row: {
            class: 'text-center font-medium text-slate-600',
            slot: 'ranking',
        },
        is_sticky: false,
        is_exportable: true,
        exportTransformer: (item, index) => ({
            'Ranking': (pagination.value.currentPage - 1) * pagination.value.perPage + index + 1,
        }),
        group: 'basic',
    },
    {
        id: 'creator',
        key: 'superview',
        header: {
            label: 'Creator',
            is_sortable: false,
            is_hideable: false,
            class: 'w-1/2',
        },
        row: {
            class: '',
            slot: 'creator',
            slotTransformer: (item) => ({
                id: item?.superview?._id || item?._id,
                name: item?.superview?.normalized?.name || item?.name,
                display_name: item?.superview?.normalized?.display_name || item?.display_name,
                logo: item?.superview?.normalized?.avatar_url || item?.avatar_url,
            }),
        },
        is_sticky: true,
        is_exportable: true,
        group: 'basic',
    },
    {
        id: 'country',
        key: 'superview.normalized.country',
        header: {
            label: 'Country',
            is_sortable: false,
            is_hideable: false,
        },
        row: {
            class: 'text-center',
            slot: 'country',
        },
        is_exportable: true,
        group: 'demographics',
    },
    {
        id: 'followers',
        key: 'crossPlatformProfiles',
        header: {
            label: 'Followers',
            is_sortable: true,
            is_hideable: false,
            sort: {
                field: 'followers',
                direction: 'desc',
            },
        },
        row: {
            class: 'py-2',
            slot: 'followers',
        },
        is_exportable: true,
        group: 'metrics',
    },
    {
        id: 'avg_views',
        key: 'discovery.avg_views',
        header: {
            label: 'Avg Views',
            is_sortable: platform.value !== 'twitch', // Not sortable on Twitch
            is_hideable: true,
            sort: {
                field: 'avg_views',
                direction: 'desc',
            },
        },
        row: {
            class: 'py-2 text-center',
            slot: 'avg_views',
        },
        is_exportable: true,
        group: 'metrics',
    },
    {
        id: 'avg_viewers',
        key: 'discovery.avg_viewers',
        header: {
            label: 'Avg CCV',
            is_sortable: platform.value === 'twitch' || contentType.value === 'stream', // Sortable on Twitch or when stream content type
            is_hideable: true,
            sort: {
                field: 'avg_viewers',
                direction: 'desc',
            },
        },
        row: {
            class: 'py-2 text-center',
            slot: 'avg_viewers',
        },
        is_exportable: true,
        group: 'metrics',
    },
    {
        id: 'industries',
        key: 'classifications',
        header: {
            label: 'Industries',
            is_sortable: false,
            is_hideable: false,
        },
        row: {
            class: 'py-2',
            slot: 'industries',
        },
        is_exportable: true,
        group: 'demographics',
    }
])

// Dynamically enable columns based on content type and platform
const enabledColumns = computed(() => {
    const baseColumns = ['ranking', 'creator', 'country', 'followers']
    const metricsColumns = []
    const endColumns = ['industries']

    // Twitch only has avg_viewers (no avg_views)
    if (platform.value === 'twitch') {
        metricsColumns.push('avg_viewers')
    } else {
        // Other platforms always show avg_views
        metricsColumns.push('avg_views')

        // Add avg_viewers for stream content types
        if (contentType.value === 'stream') {
            metricsColumns.push('avg_viewers')
        }
    }

    return [...baseColumns, ...metricsColumns, ...endColumns]
})

// Paging configuration - limit to exactly 50 creators on 1 page
const pagination = ref({
    perPage: 50, // 50 creators total
    currentPage: 1,
    totalRecords: 0, // Will be set from actual API response
    totalPages: 1, // Always exactly 1 page
    sort: [
        {
            field: 'followers',
            direction: 'desc',
            source: 'default',
        },
    ],
})

const fetchCategories = async () => {
    try {
        console.log('Fetching categories for industry:', industryName.value)

        // Use the currently selected content type
        const currentContentType = contentType.value || platformContentTypeMap[platform.value]

        const filters = [
                {
                    id: 'platform_id',
                    value: platform.value,
                },
                {
                    id: 'content_type',
                    value: currentContentType,
                },
                {
                    id: 'day_range',
                    value: 90,
                },
                {
                    id: 'parent',
                    value: [industryName.value] // This is the key - filter by parent industry
                }
            ]

        console.log('Category API request filters:', filters)

        const response = await axios.post(`/discovery/${platform.value}/codes/classifications_category`, {
            filters,
            pagination: {
                limit: 100,
                skip: 0,
                sort: [{
                    field: `metrics.platforms.${platform.value}.${currentContentType}.day_365`,
                    direction: 'desc',
                }],
            }
        })

        console.log('Category API response:', response.data)

        if (response.data.payload.codes) {
            // Filter and sort categories - "other" always at the end
            availableCategories.value = response.data.payload.codes
                .filter(category => category.code !== null)
                .sort((a, b) => {
                    // Push "other" to the end
                    if (a.code === 'other' || a.label?.toLowerCase() === 'other') return 1
                    if (b.code === 'other' || b.label?.toLowerCase() === 'other') return -1

                    // Otherwise maintain current order
                    return 0
                })
            console.log('Available categories:', availableCategories.value)
        }
    } catch (error) {
        console.error('Error fetching categories:', error)
        console.error('Error details:', error.response?.data)
        availableCategories.value = []
    }
}

// Function to fetch cross-platform data for creators
const fetchCreatorsCrossPlatformData = async (creatorIds) => {
    try {
        console.log('Fetching cross-platform data for creators:', creatorIds)

        const response = await axios.post(`/affinity/${platform.value}`, {
            channel_ids: creatorIds
        })

        console.log('Cross-platform affinity response:', response.data)
        console.log('Response payload:', response.data.payload)

        // Extract pivots data which contains cross-platform profiles
        const pivots = response.data.payload?.pivots || []
        console.log('Pivots found:', pivots.length, pivots)

        // Check if we have analysis data with biographies
        const analysis = response.data.payload?.analysis || []
        console.log('Analysis data (contains biographies):', analysis)

        // Create a map of creator_id to biography data using source_ids matching
        const biographyMap = {}
        analysis.forEach((analysisItem, analysisIndex) => {
            console.log(`Processing analysis item ${analysisIndex}:`, analysisItem)

            if (analysisItem && analysisItem.biography && analysisItem.source_ids) {
                console.log(`Found biography and source_ids in analysis ${analysisIndex}`)
                console.log('Source IDs:', analysisItem.source_ids)

                // Find source_ids that match our current platform
                const matchingSources = analysisItem.source_ids.filter(source =>
                    source.platform_id === platform.value
                )

                console.log(`Matching sources for ${platform.value}:`, matchingSources)

                if (matchingSources.length > 0) {
                    // Add biography for each matching platform_user_id
                    matchingSources.forEach((source, sourceIndex) => {
                        if (source.platform_user_id) {
                            console.log(`Found platform_user_id ${sourceIndex + 1}/${matchingSources.length}: ${source.platform_user_id}`)

                            // Use the platform_user_id as the key for biography mapping
                            biographyMap[source.platform_user_id] = analysisItem.biography
                            console.log(`Added biography to map for platform_user_id ${source.platform_user_id}`)
                        }
                    })
                } else {
                    console.warn(`No matching source_ids found for ${platform.value} in analysis ${analysisIndex}`)
                }
            } else {
                console.log(`No biography or source_ids found in analysis ${analysisIndex}`)
            }
        })

        console.log('Biography map:', biographyMap)

        // Create a map of creator_id to classifications data using source_ids matching
        const classificationsMap = {}
        analysis.forEach((analysisItem, analysisIndex) => {
            console.log(`Processing classifications for analysis item ${analysisIndex}:`, analysisItem)

            if (analysisItem && analysisItem.classifications && analysisItem.source_ids) {
                console.log(`Found classifications and source_ids in analysis ${analysisIndex}`)
                console.log('Classifications:', analysisItem.classifications)

                // Find source_ids that match our current platform
                const matchingSources = analysisItem.source_ids.filter(source =>
                    source.platform_id === platform.value
                )

                if (matchingSources.length > 0) {
                    // Group classifications by topic (industry)
                    const groupedClassifications = {}
                    analysisItem.classifications.forEach(classification => {
                        const topic = classification.topic
                        const category = classification.category

                        if (!groupedClassifications[topic]) {
                            groupedClassifications[topic] = []
                        }

                        // Only add category if it's not already in the array for this topic
                        if (!groupedClassifications[topic].includes(category)) {
                            groupedClassifications[topic].push(category)
                        }
                    })

                    // Add classifications for each matching platform_user_id
                    matchingSources.forEach(source => {
                        if (source.platform_user_id) {
                            console.log(`Added classifications to map for platform_user_id ${source.platform_user_id}:`, groupedClassifications)
                            classificationsMap[source.platform_user_id] = groupedClassifications
                        }
                    })
                } else {
                    console.warn(`No matching source_ids for ${platform.value} in analysis ${analysisIndex}`)
                }
            } else {
                console.log(`No classifications or source_ids found in analysis ${analysisIndex}`)
            }
        })

        console.log('Classifications map:', classificationsMap)

        // Create a map of creator_id to cross-platform profiles
        const crossPlatformMap = {}

        pivots.forEach((pivot, index) => {
            if (pivot && pivot.profiles) {
                // Group profiles by platform_id for easier access
                const profilesByPlatform = _.groupBy(pivot.profiles, 'platform_id')

                // Use the main platform user ID as the key to match with discovery results
                const mainProfile = pivot.profiles.find(p => p.platform_id === platform.value)

                if (mainProfile) {
                    const key = mainProfile.platform_user_id || mainProfile.id || mainProfile._id || mainProfile.user_id
                    if (key) {
                        crossPlatformMap[key] = profilesByPlatform
                    }
                }
            }
        })

        console.log('Final cross-platform data map:', crossPlatformMap)
        console.log('Map keys:', Object.keys(crossPlatformMap))
        return { crossPlatformMap, biographyMap, classificationsMap }
    } catch (error) {
        console.error('Error fetching cross-platform data:', error)
        console.error('Error details:', error.response?.data)
        return { crossPlatformMap: {}, biographyMap: {}, classificationsMap: {} }
    }
}

const fetchCreators = async () => {
    isLoadingCreators.value = true

    try {
        // Build query filters using the correct field names from DiscoverPage
        const query = {
            classifications: {
                hierarchy: {
                    topic: null,
                    category: null,
                }
            }
        }

        // Add industry filter using correct field name
        if (industryName.value) {
            query.classifications.hierarchy.topic = industryName.value
        }

        // Add category filter if specified using correct field name
        if (categoryName.value) {
            query.classifications.hierarchy.category = categoryName.value
        }

        query.classifications.conjunction = '$and'

        // Use the reactive contentType and dayRange values
        const body = {
            collection: {
                content_type: contentType.value || platformContentTypeMap[platform.value],
                day_range: String(dayRange.value), // Convert to string for API
            },
            query,
            pagination: {
                limit: pagination.value.perPage,
                skip: Math.max((pagination.value.currentPage - 1) * pagination.value.perPage, 0),
                sort: pagination.value.sort,
            },
        }

        // Keep sort field as 'followers' for all platforms

        const response = await axios.post(`/discovery/${platform.value}`, body)

        if (response.data.payload) {
            creators.value = response.data.payload.creators || []

            // Show actual total count from backend
            const actualCount = response.data.payload.count || 0
            totalCount.value = actualCount

            // Update paging data - always 1 page with actual count
            pagination.value.totalRecords = actualCount
            pagination.value.totalPages = 1

            // Now fetch cross-platform data separately (don't await this)
            if (creators.value.length > 0) {
                fetchCrossPlatformData()
            }
        }

        console.log(response.data.payload)
    } catch (error) {
        console.error('Error fetching creators:', error)
        creators.value = []
        totalCount.value = 0
    } finally {
        isLoadingCreators.value = false
    }
}

// Separate function to fetch cross-platform data and biographies
const fetchCrossPlatformData = async () => {
    if (creators.value.length === 0) return

    isLoadingCrossPlatform.value = true

    try {
        // Extract creator IDs for the affinity API call
        const creatorIds = creators.value.map(creator => creator.superview?._id || creator._id).filter(Boolean)

        console.log('Fetching cross-platform data for creator IDs:', creatorIds)

        // Get cross-platform data, biographies, and classifications
        const { crossPlatformMap, biographyMap, classificationsMap } = await fetchCreatorsCrossPlatformData(creatorIds)

        // Merge cross-platform data and biographies with creators
        creators.value = creators.value.map((creator, index) => {
            const creatorId = creator.superview?._id || creator._id
            const platformUserId = creator.id || creator.platform_user_id
            const platformProfiles = crossPlatformMap[creatorId] || {}

            // Preserve the original platform follower count from discovery API
            // Don't let affinity API override it
            const originalFollowerCount = creator.superview?.followers || creator.followers
            if (originalFollowerCount && platformProfiles[platform.value]) {
                // Keep original follower count for current platform
                platformProfiles[platform.value].forEach(profile => {
                    profile.originalFollowers = originalFollowerCount
                })
            }

            // Try to match biography by platform_user_id - biography map uses platform_user_ids as keys
            let biography = null
            const creatorPlatformUserId = creator.superview?._id || creator._id || creator.id

            if (creatorPlatformUserId) {
                biography = biographyMap[creatorPlatformUserId]
            }

            // Try to match classifications by platform_user_id
            let classifications = null
            if (creatorPlatformUserId) {
                classifications = classificationsMap[creatorPlatformUserId]
            }

            // Debug logging for data matching
            console.log(`Creator ID: ${creatorPlatformUserId}, Biography found: ${!!biography}, Classifications found: ${!!classifications}`)

            return {
                ...creator,
                crossPlatformProfiles: platformProfiles,
                biography: biography || null,
                classifications: classifications || {},
                originalFollowerCount // Store original count for reference
            }
        })

        console.log('Creators updated with cross-platform data')
    } catch (error) {
        console.error('Error fetching cross-platform data:', error)
    } finally {
        isLoadingCrossPlatform.value = false
    }
}

// Pagination and filter handlers
const onPagingChange = (updatedPaging) => {
    Object.assign(pagination.value, updatedPaging)
    fetchCreators()
}

const onFilterChange = (updatedSort) => {
    Object.assign(pagination.value.sort, updatedSort)
    fetchCreators()
}

// Ranking calculation function with debugging
const getRanking = (index) => {

    if (index === undefined || index === null) {
        console.error('Index is undefined/null')
        return '?'
    }

    if (!pagination.value.currentPage || !pagination.value.perPage) {
        console.error('Pagination values missing:', pagination.value)
        return '?'
    }

    const ranking = (pagination.value.currentPage - 1) * pagination.value.perPage + index + 1
    return ranking
}

// Helper functions for displaying cross-platform followers
const getFollowersData = (creator) => {
    const crossPlatformProfiles = creator.crossPlatformProfiles || {}
    const originalFollowerCount = creator.superview?.normalized?.followers || creator.followers

    // Always show the current platform first with original follower count
    const platformEntries = []

    // Add current platform first with original discovery API follower count
    if (originalFollowerCount) {
        platformEntries.push({
            platformId: platform.value,
            followers: originalFollowerCount,
            profile: {
                followers: originalFollowerCount,
                platform_id: platform.value
            }
        })
    }

    // Add other platforms from cross-platform data (if loaded)
    if (Object.keys(crossPlatformProfiles).length > 0) {
        Object.entries(crossPlatformProfiles)
            .filter(([platformId, profiles]) => {
                // Skip current platform since we already added it
                return platformId !== platform.value && profiles && profiles.length > 0
            })
            .forEach(([platformId, profiles]) => {
                const profile = profiles[0] // Take first profile for each platform
                const followerCount = profile.followers

                // Only add platforms with valid follower counts
                if (followerCount !== null && followerCount !== undefined) {
                    platformEntries.push({
                        platformId,
                        followers: followerCount,
                        profile: {
                            ...profile,
                            followers: followerCount
                        }
                    })
                }
            })
    }

    // Sort: current platform first, then by follower count descending
    platformEntries.sort((a, b) => {
        if (a.platformId === platform.value) return -1
        if (b.platformId === platform.value) return 1
        return (b.followers || 0) - (a.followers || 0)
    })

    // Limit to 4 platforms, show "+X more" for the rest
    const limitedEntries = platformEntries.slice(0, 4)

    // Convert back to object format for template
    const result = {}
    limitedEntries.forEach(entry => {
        result[entry.platformId] = entry.profile
    })

    return result
}

const getHiddenPlatformsCount = (creator) => {
    const crossPlatformProfiles = creator.crossPlatformProfiles || {}
    const originalFollowerCount = creator.superview?.normalized?.followers || creator.followers

    // Count total valid platforms (current platform + cross-platform data)
    let totalValidPlatforms = 0

    // Always count current platform if it has follower data
    if (originalFollowerCount) {
        totalValidPlatforms += 1
    }

    // Count other platforms from cross-platform data
    if (Object.keys(crossPlatformProfiles).length > 0) {
        const otherValidPlatforms = Object.keys(crossPlatformProfiles).filter(platformId => {
            if (platformId === platform.value) return false // Skip current platform

            const profiles = crossPlatformProfiles[platformId]
            if (!profiles || profiles.length === 0) return false

            const profile = profiles[0]
            const followerCount = profile.followers

            // Only count platforms with non-null followers
            return followerCount !== null && followerCount !== undefined
        }).length

        totalValidPlatforms += otherValidPlatforms
    }

    return Math.max(0, totalValidPlatforms - 4)
}

const getPlatformIcon = (platformId) => {
    const iconMap = {
        youtube: 'youtube',
        twitch: 'twitch',
        tiktok: 'tiktok',
        twitter: 'x-twitter',
        instagram: 'instagram'
    }
    return iconMap[platformId] || platformId
}

const getPlatformBgClass = (platformId) => {
    const bgMap = {
        youtube: 'bg-red-600',
        twitch: 'bg-purple-600',
        tiktok: 'bg-black',
        twitter: 'bg-x',
        instagram: 'bg-pink-600'
    }
    return bgMap[platformId] || 'bg-gray-500'
}

const formatFollowerCount = (count) => {
    return proxy.$filters.formatWholeNumberWithUnit(count || 0)
}

const formatNumber = (number) => {
    return proxy.$filters.formatWholeNumberWithUnit(number || 0)
}

// Simple helper function to get country code
const getCountryCode = (creator) => {
    const country = creator.superview?.normalized?.country ||
                   creator.superview?.channel?.country ||
                   creator.superview?.channel?.snippet?.country ||
                   creator.country

    if (!country) {
        return '-'
    }

    return country.toUpperCase()
}

// Helper function to get row background class for top 3 creators
const getRowBackgroundClass = (index) => {
    // Only apply special backgrounds on the first page
    if (pagination.value.currentPage !== 1) {
        return ''
    }

    const ranking = index + 1

    switch (ranking) {
        case 1:
            return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200' // Gold
        case 2:
            return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200' // Silver
        case 3:
            return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200' // Bronze
        default:
            return ''
    }
}

// Helper function to create biography snippet (always 3 lines)
const getBiographySnippet = (biography) => {
    if (!biography || typeof biography !== 'string') {
        return ''
    }

    const maxLength = 300 // Approximately 3 lines of text
    const trimmed = biography.trim()

    if (trimmed.length <= maxLength) {
        return trimmed
    }

    // Find the last space before the max length to avoid cutting words
    const lastSpace = trimmed.lastIndexOf(' ', maxLength)
    const cutPoint = lastSpace > 0 ? lastSpace : maxLength

    return trimmed.substring(0, cutPoint) + '...'
}

// Helper function to display industries for a creator
const getIndustriesDisplay = (creator) => {
    const classifications = creator.classifications || {}

    if (Object.keys(classifications).length === 0) {
        return 'No classifications'
    }

    // Find the current searched industry/category to show first
    const searchedIndustry = industryName.value
    const searchedCategory = categoryName.value

    // Check if creator has the searched industry/category
    const searchedCategories = classifications[searchedIndustry] || []
    const hasSearchedCategory = searchedCategory ? searchedCategories.includes(searchedCategory) : true

    if (hasSearchedCategory) {
        // Show the searched industry first
        const displayCategories = searchedCategory ? [searchedCategory] : searchedCategories.slice(0, 2)

        // Count total industries excluding the main one
        const totalIndustries = Object.keys(classifications).length
        const otherIndustriesCount = totalIndustries - 1

        // Count total categories across all industries
        const totalCategories = Object.values(classifications).reduce((sum, categories) => sum + categories.length, 0)
        const displayedCategories = displayCategories.length
        const remainingCategories = totalCategories - displayedCategories

        let displayText = `${searchedIndustry}`
        if (displayCategories.length > 0) {
            displayText += ` - ${displayCategories.join(', ')}`
        }

        // Add "+X more" if there are additional classifications
        if (remainingCategories > 0) {
            const moreCount = remainingCategories
            displayText += ` +${moreCount} more`
        }

        return displayText
    }

    // Fallback if searched industry/category not found (shouldn't happen)
    const firstIndustry = Object.keys(classifications)[0]
    const firstCategories = classifications[firstIndustry].slice(0, 2)
    return `${firstIndustry} - ${firstCategories.join(', ')}`
}

// Helper function to get industry emoji icon
const getIndustryIcon = (industryName) => {
    const iconMap = {
        // Based on common industry classifications
        'Gaming': '🎮',
        'Music': '🎵',
        'Sports': '⚽',
        'Technology': '💻',
        'Fashion': '👗',
        'Food': '🍕',
        'Travel': '✈️',
        'Education': '🎓',
        'Health': '⚕️',
        'Entertainment': '🎬',
        'News': '📰',
        'Automotive': '🚗',
        'Beauty': '💄',
        'Fitness': '💪',
        'Business': '💼',
        'Art': '🎨',
        'Science': '🔬',
        'Comedy': '😂',
        'Lifestyle': '🌟',
        'Finance': '💰',
        'Politics': '🏛️',
        'Animals': '🐾',
        'DIY': '🔧',
        'Photography': '📸',
        'Cooking': '👨‍🍳',
        'Parenting': '👶',
        'Relationships': '💕',
        'History': '📚',
        'Religion': '⛪',
        'Environment': '🌱'
    }

    // Normalize the industry name for lookup (handle case variations)
    const normalizedName = Object.keys(iconMap).find(key =>
        key.toLowerCase() === industryName.toLowerCase()
    )

    return iconMap[normalizedName] || '🏭' // Default factory icon
}

// Helper functions for industries column display
const getMainCategories = (creator) => {
    const classifications = creator.classifications || {}
    const searchedIndustry = industryName.value
    const searchedCategory = categoryName.value

    const searchedCategories = classifications[searchedIndustry] || []

    // If viewing a specific category, show only that one
    // Otherwise show first 2 categories from the searched industry
    return searchedCategory ? [searchedCategory] : searchedCategories.slice(0, 2)
}

const getMainIndustryText = (creator) => {
    const classifications = creator.classifications || {}
    const searchedIndustry = industryName.value
    const searchedCategory = categoryName.value

    const searchedCategories = classifications[searchedIndustry] || []
    const displayCategories = searchedCategory ? [searchedCategory] : searchedCategories.slice(0, 2)

    let text = searchedIndustry
    if (displayCategories.length > 0) {
        text += ` - ${displayCategories.join(', ')}`
    }

    return text
}

const hasMoreClassifications = (creator) => {
    const classifications = creator.classifications || {}
    // Count total individual classifications across all industries
    const totalClassifications = Object.values(classifications).reduce((sum, categories) => sum + categories.length, 0)
    return totalClassifications > 1
}

const getMoreClassificationsText = (creator) => {
    const classifications = creator.classifications || {}
    // Count total individual classifications across all industries, minus 1
    const totalClassifications = Object.values(classifications).reduce((sum, categories) => sum + categories.length, 0)
    const moreCount = totalClassifications - 1
    return `+${moreCount} more`
}

const toggleIndustriesDropdown = (creator) => {
    const creatorId = getCreatorId(creator)
    expandedCreatorId.value = expandedCreatorId.value === creatorId ? null : creatorId
}

const getCreatorId = (creator) => {
    return creator.superview?._id || creator._id || creator.id
}

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
    // Check if clicked element is inside any dropdown container or dropdown itself
    const isInsideContainer = event.target.closest('.industries-dropdown-container')
    const isInsideDropdown = event.target.closest('.industries-dropdown')

    if (!isInsideContainer && !isInsideDropdown) {
        expandedCreatorId.value = null
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    // Initialize contentType based on current platform
    contentType.value = platformContentTypeMap[platform.value]
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})

// Platform change handler
const onPlatformClick = (newPlatform) => {
    // Update the platform
    platform.value = newPlatform

    // Update contentType to default for new platform
    contentType.value = platformContentTypeMap[newPlatform]

    // Reset pagination to first page when changing platforms
    pagination.value.currentPage = 1

    // Reset sort to default (followers descending) for the new platform
    pagination.value.sort = [
        {
            field: 'followers',
            direction: 'desc',
            source: 'default',
        },
    ]

    // Reset loading states
    isLoadingCreators.value = false
    isLoadingCrossPlatform.value = false

    // Refetch data for new platform
    fetchCategories()
    fetchCreators()
}

// Modal close handler
const onCreatorProfileModalClose = () => {
    const query = { ...route.query }
    delete query.modal
    delete query.platform_id
    delete query.platform_user_id
    router.push({ path: route.path, query })
}

// Function to navigate to discovery page with appropriate filters
const goToDiscoveryPage = async () => {
    try {
        console.log('Creating discovery search with industry/category filters...')

        // Set content_type based on platform (matching Discovery page logic)
        let contentType = 'video' // Default for YouTube, TikTok, Instagram
        if (platform.value === 'twitch') {
            contentType = 'stream'
        } else if (platform.value === 'twitter') {
            contentType = 'post'
        }

        // Build the filter structure that Discovery expects
        const requestBody = {
            collection: {
                content_type: contentType,
                day_range: '90', // 90 day range to match current page
            },
            query: {
                classifications: {
                    hierarchy: {
                        topic: industryName.value,      // Industry name
                        category: categoryName.value || null, // Category name (if on category page)
                    },
                    conjunction: '$and',
                    primary_only: true,
                },
                // Try to include sorting in query so it gets restored by getPreviousSearch
                _sort: {
                    field: 'followers',
                    direction: 'desc',
                    source: 'industry_page'
                }
            },
            pagination: {
                limit: 15, // Discovery page default
                skip: 0,
                sort: [{
                    field: 'followers', // Use same sort as current page
                    direction: 'desc',
                    source: 'industry_page'
                }]
            }
        }

        console.log('Discovery request body:', requestBody)

        // Make the discovery API call to get a request hash
        const response = await axios.post(`/discovery/${platform.value}`, requestBody)
        console.log('Full API response:', response.data)

        const requestHash = response.data.payload?.requestHash

        if (requestHash) {
            console.log('Got request hash:', requestHash)

            // Navigate to discovery page with the search history ID
            // This will load the exact same filters we just used
            router.push({
                name: 'discovery',
                query: {
                    id: requestHash, // This triggers Discovery to load our filters via getPreviousSearch()
                    sort: 'followers', // Custom sort parameter to override Discovery's default
                    sortDirection: 'desc'
                }
            })
        } else {
            console.warn('No request hash returned, falling back to basic navigation')
            // Fallback to basic navigation if request hash is missing
            router.push({
                name: 'discovery',
                query: {
                    platform: platform.value
                }
            })
        }

    } catch (error) {
        console.error('Error creating discovery search:', error)

        // Fallback to basic navigation on error
        router.push({
            name: 'discovery',
            query: {
                platform: platform.value
            }
        })
    }
}

// Watch for route params changes to refetch data when switching categories
// Only trigger when industry_name or category_name actually change
// Watch for content type changes
watch(contentType, () => {
    pagination.value.currentPage = 1
    fetchCreators()
})
// Watch for day range changes
watch(dayRange, () => {
    pagination.value.currentPage = 1
    fetchCreators()
})

watch(() => ({
    industry_name: route.params.industry_name,
    category_name: route.params.category_name
}), (newParams, oldParams) => {
    // Only refetch if the actual params changed (not query params like modal state)
    if (!oldParams ||
        newParams.industry_name !== oldParams.industry_name ||
        newParams.category_name !== oldParams.category_name) {
        fetchCategories()
        fetchCreators()
    }
}, { immediate: true })

// onMounted removed - the watcher with immediate: true handles initial load
</script>
